import { useMemo, useState } from 'react';
import useWindowEventListener from '@/shared/hooks/useWindowEventListener';

type Payload<T> = {
  dispatcher: string;
  newValue: T;
};

declare global {
  interface WindowEventMap {
    [key: `local-storage-update-${string}`]: CustomEvent<Payload<unknown>>;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function useLocalStorage<T>(key: string, parse: (value: any) => T) {
  const [value, setValue] = useState<T>(() => {
    if (typeof window !== 'undefined') {
      const item = window.localStorage.getItem(`cf.${key}`);
      return parse(item && JSON.parse(item));
    }

    return parse(null);
  });

  const dispatcher = useMemo(() => {
    try {
      return crypto.randomUUID();
    } catch {
      // crypto is only available in https environments
      return Math.random().toString();
    }
  }, []);

  const eventName = `local-storage-update-${key}` as const;

  useWindowEventListener(eventName, (e) => {
    if (e.detail.dispatcher === dispatcher) return;
    setValue((e as CustomEvent<Payload<T>>).detail.newValue);
  });

  const save = (newValue: T) => {
    setValue(newValue);
    const newValueString = JSON.stringify(newValue);
    window.dispatchEvent(
      new CustomEvent<Payload<T>>(eventName, {
        detail: {
          dispatcher,
          newValue,
        },
      }),
    );
    window.localStorage.setItem(`cf.${key}`, newValueString);
  };

  return [value, save] as const;
}
